/**
 * Short-lived map effects that make combat readable from above: an aim line from attacker to
 * target while the dice tumble, the attacker's lunge (or a missile) when they land, floating
 * damage and healing numbers, and a red flash on whoever was hit. Effects can be scheduled ahead
 * (`delay`) so they line up with the dice tray, and pulled forward when the matching roll lands.
 */

type Pos = { x: number; y: number };

interface Floater {
  text: string;
  color: string;
  creatureId?: string;
  /** Last known square, used once the creature is gone. */
  at: Pos;
  start: number;
  size: number;
  /** Damage: the creature flashes red when the number appears. */
  hit: boolean;
}

interface Lunge {
  dx: number;
  dy: number;
  start: number;
}

interface Missile {
  from: Pos;
  to: Pos;
  start: number;
  color: string;
}

interface Aim {
  actorId: string;
  targetId: string;
  color: string;
  start: number;
  until: number;
}

export const FLOAT_MS = 1400;
const LUNGE_MS = 300;
const FLASH_MS = 450;
const MISSILE_MS = 240;

export class Effects {
  private floaters: Floater[] = [];
  private lunges = new Map<string, Lunge>();
  private missiles: Missile[] = [];
  private aims: Aim[] = [];

  /** Text that rises and fades above a creature (or above a square, once the creature is gone). */
  float(text: string, color: string, at: Pos, opts: { creatureId?: string; delay?: number; size?: number; hit?: boolean } = {}): void {
    const start = performance.now() + (opts.delay ?? 0);
    this.floaters.push({ text, color, creatureId: opts.creatureId, at: { ...at }, start: this.stagger(opts.creatureId, start), size: opts.size ?? 1, hit: !!opts.hit });
  }

  /** Numbers landing on one creature at the same moment rise one after another instead of overlapping. */
  private stagger(creatureId: string | undefined, start: number): number {
    let t = start;
    for (const f of this.floaters) if (f.creatureId === creatureId && Math.abs(f.start - t) < 220) t = f.start + 220;
    return t;
  }

  /**
   * A roll for this creature just landed: show its earliest damage number now rather than at the
   * scheduled estimate. Returns whether one was waiting.
   */
  release(creatureId: string, opts: { size?: number } = {}): boolean {
    const now = performance.now();
    const waiting = this.floaters.filter((f) => f.creatureId === creatureId && f.hit && f.start > now).sort((a, b) => a.start - b.start)[0];
    if (!waiting) return false;
    waiting.start = this.stagger(creatureId, now);
    if (opts.size) waiting.size = opts.size;
    return true;
  }

  /** The attacker darts toward its target and back (`reach` in squares). */
  lunge(creatureId: string, from: Pos, to: Pos, reach = 0.38): void {
    const dx = to.x - from.x, dy = to.y - from.y;
    const len = Math.hypot(dx, dy) || 1;
    this.lunges.set(creatureId, { dx: (dx / len) * reach, dy: (dy / len) * reach, start: performance.now() });
  }

  missile(from: Pos, to: Pos, color: string): void {
    this.missiles.push({ from: { ...from }, to: { ...to }, start: performance.now(), color });
  }

  /** A line from attacker to target while the attack roll is in the air. */
  aim(actorId: string, targetId: string, color: string, ms: number): void {
    this.aims = this.aims.filter((a) => a.actorId !== actorId);
    const now = performance.now();
    this.aims.push({ actorId, targetId, color, start: now, until: now + ms });
  }

  /** The attack landed (or missed): its aim line goes away. */
  endAim(actorId: string): void {
    this.aims = this.aims.filter((a) => a.actorId !== actorId);
  }

  /** How far a creature's token is drawn from its square right now, in squares. */
  offset(id: string, now: number): Pos {
    const l = this.lunges.get(id);
    if (!l || now < l.start) return { x: 0, y: 0 };
    const t = (now - l.start) / LUNGE_MS;
    if (t >= 1) {
      this.lunges.delete(id);
      return { x: 0, y: 0 };
    }
    // Quick strike out, slower return.
    const k = t < 0.35 ? Math.sin((t / 0.35) * (Math.PI / 2)) : Math.cos(((t - 0.35) / 0.65) * (Math.PI / 2));
    return { x: l.dx * k, y: l.dy * k };
  }

  /** How strongly a creature flashes red (0 to 1): it was just hit. */
  flash(id: string, now: number): number {
    let best = 0;
    for (const f of this.floaters) {
      if (!f.hit || f.creatureId !== id || now < f.start || now - f.start > FLASH_MS) continue;
      best = Math.max(best, 1 - (now - f.start) / FLASH_MS);
    }
    return best;
  }

  /** Floaters to draw now: square coordinates of the text's centre, plus colour, opacity and scale. */
  activeFloaters(now: number, where: (id: string) => Pos | null): { x: number; y: number; text: string; color: string; alpha: number; scale: number }[] {
    this.floaters = this.floaters.filter((f) => now - f.start < FLOAT_MS);
    const out = [];
    for (const f of this.floaters) {
      if (now < f.start) continue;
      const t = (now - f.start) / FLOAT_MS;
      const p = (f.creatureId && where(f.creatureId)) || f.at;
      f.at = { x: p.x, y: p.y };
      // Pops in large, settles, then drifts up and fades.
      const pop = t < 0.1 ? 0.6 + (t / 0.1) * 0.65 : Math.max(1, 1.25 - (t - 0.1) * 1.2);
      out.push({ x: p.x + 0.5, y: p.y - 0.12 - easeOut(t) * 0.7, text: f.text, color: f.color, alpha: t > 0.65 ? 1 - (t - 0.65) / 0.35 : 1, scale: pop * f.size });
    }
    return out;
  }

  /** Missiles in flight: the head and the end of a short trail, in square coordinates (centres). */
  activeMissiles(now: number): { x: number; y: number; tx: number; ty: number; color: string }[] {
    this.missiles = this.missiles.filter((m) => now - m.start < MISSILE_MS);
    return this.missiles.filter((m) => now >= m.start).map((m) => {
      const t = (now - m.start) / MISSILE_MS;
      const at = (k: number) => ({ x: m.from.x + (m.to.x - m.from.x) * k + 0.5, y: m.from.y + (m.to.y - m.from.y) * k + 0.5 });
      const head = at(t), tail = at(Math.max(0, t - 0.4));
      return { x: head.x, y: head.y, tx: tail.x, ty: tail.y, color: m.color };
    });
  }

  /** Aim lines to draw now, with how long each has been shown (for the dash animation). */
  activeAims(now: number): { actorId: string; targetId: string; color: string; age: number }[] {
    this.aims = this.aims.filter((a) => now < a.until);
    return this.aims.map((a) => ({ actorId: a.actorId, targetId: a.targetId, color: a.color, age: now - a.start }));
  }
}

const easeOut = (t: number) => 1 - (1 - t) ** 2;
